import { Link } from "react-router-dom";
import { openConsentSettings } from "../lib/consent";
import { Phone, Mail, MapPin, Linkedin } from "lucide-react";
import { NAV_LINKS, CONTACT, SOCIAL } from "../lib/site";

const COMPLIANCE = ["ITAR Registered", "CMMC Level 2", "MIL-STD", "ISO 9001", "ISO 14001", "CAGE 04099"];

export default function Footer() {
  return (
    <footer
      className="relative bg-white border-t border-line"
      data-testid="site-footer"
    >
      {/* ── INFO BAND · BONE · sitemap · operations ─────────── */}
      <div
        className="relative bg-bone border-b border-line"
        data-testid="footer-info"
      >
        <div className="max-w-[1400px] mx-auto px-6 md:px-12 py-16 md:py-20">
          <div className="grid md:grid-cols-12 gap-10 lg:gap-14">
            {/* Sitemap */}
            <div className="md:col-span-6">
              <div className="font-mono text-[14px] tracking-[0.32em] text-signal uppercase mb-5">
                Site Directory
              </div>
              <ul className="grid grid-cols-2 gap-y-2.5 gap-x-6">
                {NAV_LINKS.map((l) => (
                  <li key={l.to}>
                    {l.external ? (
                      <a
                        href={l.to}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-navy/75 hover:text-signal text-[14px] font-display font-medium tracking-tight transition-colors"
                        data-testid={`footer-nav-${l.label.toLowerCase()}`}
                      >
                        {l.label}
                      </a>
                    ) : (
                      <Link
                        to={l.to}
                        className="text-navy/75 hover:text-signal text-[14px] font-display font-medium tracking-tight transition-colors"
                        data-testid={`footer-nav-${l.to.replace(/\//g, "") || "home"}`}
                      >
                        {l.label}
                      </Link>
                    )}
                  </li>
                ))}
              </ul>
            </div>

            {/* Business & Operating Hours */}
            <div className="md:col-span-6">
              <div className="font-mono text-[14px] tracking-[0.32em] text-signal uppercase mb-5">
                Business &amp; Operating Hours
              </div>
              <ul className="space-y-3 text-[14px] text-navy/80">
                <li className="flex items-start gap-3">
                  <MapPin className="w-3.5 h-3.5 text-signal mt-0.5 shrink-0" strokeWidth={2} />
                  <span>1328 Winters Ave, Grand Junction, CO 81501</span>
                </li>
                <li className="flex items-start gap-3">
                  <Mail className="w-3.5 h-3.5 text-signal mt-0.5 shrink-0" strokeWidth={2} />
                  <a
                    href={`mailto:${CONTACT.email}`}
                    className="hover:text-signal transition-colors"
                    data-testid="footer-email-link"
                  >
                    {CONTACT.email}
                  </a>
                </li>
                <li className="flex items-start gap-3">
                  <Phone className="w-3.5 h-3.5 text-signal mt-0.5 shrink-0" strokeWidth={2} />
                  <a
                    href={`tel:${CONTACT.phoneTel}`}
                    className="hover:text-signal transition-colors"
                    data-testid="footer-phone-link"
                  >
                    {CONTACT.phoneDisplay}
                  </a>
                </li>
                <li className="text-steel font-mono text-[15.5px] tracking-[0.18em] uppercase pt-1 flex flex-col gap-1">
  {CONTACT.hoursMT.split("·").map((part) => (
    <span key={part}>{part.trim()}</span>
  ))}
</li>
              </ul>

              {SOCIAL.linkedin ? (
                <a
                  href={SOCIAL.linkedin}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="footer-linkedin-link"
                  className="group mt-6 inline-flex items-center gap-3 bg-navy hover:bg-signal text-white font-mono text-[14px] tracking-[0.28em] uppercase pl-3 pr-4 py-2.5 transition-colors"
                >
                  <span className="inline-flex w-6 h-6 items-center justify-center bg-white text-navy group-hover:text-signal transition-colors">
                    <Linkedin className="w-3.5 h-3.5" strokeWidth={2.2} />
                  </span>
                  <span>Follow on LinkedIn</span>
                </a>
              ) : null}

              <div className="mt-6 flex flex-wrap gap-1.5">
                {COMPLIANCE.map((c) => (
                  <span
                    key={c}
                    className="font-mono text-[14px] tracking-[0.22em] uppercase border border-navy/20 px-2.5 py-1 text-navy/90"
                    data-testid={`footer-compliance-${c.toLowerCase().replace(/\W/g, "")}`}
                  >
                    {c}
                  </span>
                ))}
              </div>
            </div>

          </div>
        </div>
      </div>

      {/* ── LEGAL STRIP · WHITE · minimal ────────────────────────────────── */}
      <div className="bg-white">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12 py-6 flex flex-col md:flex-row md:items-center md:justify-between gap-3 text-[14px] font-mono tracking-[0.32em] text-steel uppercase">
          <div>© 2026 Valor Tactical Innovation LLC. All Rights Reserved.</div>
          <div className="flex flex-wrap gap-x-6 gap-y-1">
            <Link
              to="/privacy"
              data-testid="footer-privacy-link"
              className="hover:text-signal transition-colors"
            >
              Privacy
            </Link>
            <Link
              to="/terms"
              data-testid="footer-terms-link"
              className="hover:text-signal transition-colors"
            >
              Terms of Use
            </Link>
            <button
              type="button"
              onClick={openConsentSettings}
              data-testid="footer-cookie-settings"
              className="hover:text-signal transition-colors uppercase tracking-[0.32em] font-mono text-[14px]"
            >
              Cookie settings
            </button>
          </div>
        </div>
      </div>
    </footer>
  );
}
